import React, { useState } from 'react';
import { useCreateTodo } from '../helpers/useTodosApi';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Calendar, X } from 'lucide-react';
import styles from './CreateTodoForm.module.css';

interface CreateTodoFormProps {
  sessionId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const CreateTodoForm: React.FC<CreateTodoFormProps> = ({
  sessionId,
  onCancel,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');

  const { mutate: createTodo, isPending } = useCreateTodo();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    createTodo(
      {
        sessionId,
        title,
        description: description || undefined,
        dueDate: dueDate ? new Date(dueDate) : undefined,
      },
      {
        onSuccess: () => {
          setTitle('');
          setDescription('');
          setDueDate('');
          onSuccess();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.header}>
        <span className={styles.title}>New Task</span>
        <Button variant="ghost" size="icon-sm" onClick={onCancel} type="button" aria-label="Cancel">
          <X size={16} />
        </Button>
      </div>

      <div className={styles.fields}>
        <div className={styles.fieldGroup}>
          <Input
            placeholder="What needs to be done?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
            className={styles.inputTitle}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Textarea
            placeholder="Add details (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={styles.textarea}
          />
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.dateInputWrapper}>
            <Calendar size={16} className={styles.dateIcon} />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={styles.nativeDateInput}
              aria-label="Due date"
            />
          </div>
        </div>
      </div>

      <div className={styles.actions}>
        <Button variant="ghost" onClick={onCancel} type="button" size="sm">
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!title.trim() || isPending}>
          {isPending ? 'Adding...' : 'Add Task'}
        </Button>
      </div>
    </form>
  );
};
